import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import ApiInspector from '../../../components/ApiInspector';
import { API_DOCS } from './SfridiPage.api';
import './SfridiPage.css';

// ── Icone ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface FormatoArticolo { id: number; nome: string; }

interface SfridoRecord {
  formatoArticoloId: number;
  formatoNome: string;
  righe: { quantitaDa: number; quantitaA: number | null; sfrido: number }[];
}

interface RigaLocale {
  _key: string;
  quantitaDa: number;
  quantitaA: number | null;
  infinito: boolean;
  sfrido: number;
}

// ── Helper ────────────────────────────────────────────────────────────────────

function stringaRiepilogo(righe: { quantitaDa: number; quantitaA: number | null; sfrido: number }[]): string {
  const s = [...righe]
    .sort((a, b) => a.quantitaDa - b.quantitaDa)
    .map(r => `${r.quantitaDa}-${r.quantitaA ?? '∞'}: ${r.sfrido}%`)
    .join(' | ');
  return s.length > 50 ? s.slice(0, 47) + '...' : s;
}

function ricalcolaQuantitaDa(arr: RigaLocale[]): RigaLocale[] {
  return arr.map((r, i) => {
    if (i === 0) return { ...r, quantitaDa: 0 };
    const prev = arr[i - 1];
    const prevA = prev.infinito ? null : prev.quantitaA;
    return { ...r, quantitaDa: prevA ?? r.quantitaDa };
  });
}

function righeToLocale(righe: { quantitaDa: number; quantitaA: number | null; sfrido: number }[]): RigaLocale[] {
  return righe.map(r => ({
    _key: crypto.randomUUID(),
    quantitaDa: r.quantitaDa,
    quantitaA: r.quantitaA,
    infinito: r.quantitaA === null,
    sfrido: r.sfrido,
  }));
}

function righeLocaleUguali(a: RigaLocale[], b: RigaLocale[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) => (
    r.quantitaDa === b[i].quantitaDa &&
    r.quantitaA === b[i].quantitaA &&
    r.infinito === b[i].infinito &&
    r.sfrido === b[i].sfrido
  ));
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function SfridiPage() {
  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const { can } = usePermesso();

  // Tabella principale
  const [lista, setLista] = useState<SfridoRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [modalFormatoId, setModalFormatoId] = useState<number | null>(null);
  const [modalFormatoNome, setModalFormatoNome] = useState('');
  const [modalRighe, setModalRighe] = useState<RigaLocale[]>([]);
  const [modalRigheOriginali, setModalRigheOriginali] = useState<RigaLocale[]>([]);
  const [saving, setSaving] = useState(false);

  // Autocomplete formato nel modal
  const [formatoQuery, setFormatoQuery] = useState('');
  const [formatoResults, setFormatoResults] = useState<FormatoArticolo[]>([]);
  const [formatoDropOpen, setFormatoDropOpen] = useState(false);
  const formatoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formatoDropRef = useRef<HTMLDivElement>(null);

  const isDirty = !righeLocaleUguali(modalRighe, modalRigheOriginali) ||
    (modalMode === 'create' && (modalFormatoId !== null || modalRighe.length > 0));

  // ── Carica lista ─────────────────────────────────────────────────────────────

  const caricaLista = useCallback(async () => {
    if (!pronta) { setLista([]); return; }
    setLoading(true);
    try {
      const qs = filterSocietaId ? `?societaId=${filterSocietaId}` : '';
      // @api GET /api/sfridi/lista?societaId -> [{ formatoArticoloId, formatoNome, righe: [{ quantitaDa, quantitaA, sfrido }] }]
      const data = await apiFetch<SfridoRecord[]>(`/api/sfridi/lista${qs}`);
      setLista(data);
    } catch {
      showToast('Errore nel caricamento degli sfridi', 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // ── Chiudi dropdown formato al click fuori ───────────────────────────────────

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (formatoDropRef.current && !formatoDropRef.current.contains(e.target as Node)) {
        setFormatoDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Autocomplete formato ──────────────────────────────────────────────────────

  const cercaFormati = useCallback((q: string) => {
    if (!filterSocietaId) return;
    if (formatoTimerRef.current) clearTimeout(formatoTimerRef.current);
    formatoTimerRef.current = setTimeout(async () => {
      try {
        // @api GET /api/formati-articolo?societaId&q -> [{ id, nome }]
        const data = await apiFetch<FormatoArticolo[]>(
          `/api/formati-articolo?societaId=${filterSocietaId}${q ? `&q=${encodeURIComponent(q)}` : ''}`
        );
        // Esclude formati già configurati
        const configuredIds = lista.map(r => r.formatoArticoloId);
        setFormatoResults(data.filter(f => !configuredIds.includes(f.id)));
        setFormatoDropOpen(true);
      } catch { /* silenzioso */ }
    }, 300);
  }, [filterSocietaId, lista]);

  function handleFormatoQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setFormatoQuery(q);
    setModalFormatoId(null);
    setModalFormatoNome('');
    cercaFormati(q);
  }

  function handleFormatoFocus() {
    cercaFormati(formatoQuery);
  }

  function selezionaFormato(fmt: FormatoArticolo) {
    setModalFormatoId(fmt.id);
    setModalFormatoNome(fmt.nome);
    setFormatoQuery(fmt.nome);
    setFormatoDropOpen(false);
  }

  // ── Apertura modale ──────────────────────────────────────────────────────────

  function apriCrea() {
    if (!filterSocietaId) {
      showToast('Seleziona una società per continuare', 'error');
      return;
    }
    setModalMode('create');
    setModalFormatoId(null);
    setModalFormatoNome('');
    setFormatoQuery('');
    setFormatoResults([]);
    setFormatoDropOpen(false);
    setModalRighe([]);
    setModalRigheOriginali([]);
    setSaving(false);
    setShowModal(true);
    // Carica subito i formati disponibili
    setTimeout(() => cercaFormati(''), 0);
  }

  function apriModifica(record: SfridoRecord) {
    const righeLocale = righeToLocale(record.righe);
    setModalMode('edit');
    setModalFormatoId(record.formatoArticoloId);
    setModalFormatoNome(record.formatoNome);
    setFormatoQuery(record.formatoNome);
    setFormatoResults([]);
    setFormatoDropOpen(false);
    setModalRighe(righeLocale);
    setModalRigheOriginali(righeLocale.map(r => ({ ...r })));
    setSaving(false);
    setShowModal(true);
  }

  function chiudiModal() {
    if (isDirty) {
      if (!window.confirm('Hai modifiche non salvate. Continuare?')) return;
    }
    setShowModal(false);
  }

  // ── Elimina record ────────────────────────────────────────────────────────────

  async function eliminaRecord(record: SfridoRecord) {
    if (!window.confirm(`Eliminare tutti gli sfridi per il formato "${record.formatoNome}"?`)) return;
    try {
      // @api DELETE /api/sfridi?societaId&formatoArticoloId -> { success: true }
      await apiFetch(
        `/api/sfridi?societaId=${filterSocietaId}&formatoArticoloId=${record.formatoArticoloId}`,
        { method: 'DELETE' }
      );
      showToast('Sfridi eliminati', 'success');
      caricaLista();
    } catch {
      showToast('Errore durante l\'eliminazione', 'error');
    }
  }

  // ── Gestione righe nel modal ──────────────────────────────────────────────────

  function aggiungiRange() {
    const ultima = modalRighe[modalRighe.length - 1];
    if (ultima && ultima.infinito) {
      showToast("Imposta prima il limite superiore dell'ultima riga", 'error');
      return;
    }
    const nuovaDa = ultima ? (ultima.quantitaA as number) : 0;
    setModalRighe(prev => [
      ...prev,
      { _key: crypto.randomUUID(), quantitaDa: nuovaDa, quantitaA: null, infinito: false, sfrido: 0 },
    ]);
  }

  function eliminaRiga(key: string) {
    if (modalRighe.length <= 1) return;
    setModalRighe(prev => ricalcolaQuantitaDa(prev.filter(r => r._key !== key)));
  }

  function aggiornaQuantitaA(key: string, val: string) {
    const n = val === '' ? null : parseInt(val, 10);
    setModalRighe(prev => {
      const updated = prev.map(r =>
        r._key === key ? { ...r, quantitaA: (n !== null && !isNaN(n)) ? n : null } : r
      );
      return ricalcolaQuantitaDa(updated);
    });
  }

  function aggiornaSfrido(key: string, val: string) {
    const n = val === '' ? 0 : parseInt(val, 10);
    setModalRighe(prev => prev.map(r => r._key === key ? { ...r, sfrido: isNaN(n) ? 0 : n } : r));
  }

  function toggleInfinito(key: string) {
    setModalRighe(prev => {
      const updated = prev.map(r => {
        if (r._key !== key) return r;
        const newInf = !r.infinito;
        return { ...r, infinito: newInf, quantitaA: newInf ? null : null };
      });
      return ricalcolaQuantitaDa(updated);
    });
  }

  // ── Validazione ───────────────────────────────────────────────────────────────

  function valida(): string | null {
    if (modalMode === 'create' && !modalFormatoId) return 'Seleziona un formato articolo';
    if (modalRighe.length === 0) return 'Aggiungi almeno un range';
    for (let i = 0; i < modalRighe.length; i++) {
      const r = modalRighe[i];
      const isUltima = i === modalRighe.length - 1;
      if (!Number.isInteger(r.sfrido) || r.sfrido < 0 || r.sfrido > 100) {
        return `Riga ${i + 1}: sfrido deve essere un intero tra 0 e 100`;
      }
      if (!isUltima && r.infinito) {
        return `Riga ${i + 1}: solo l'ultima riga può avere limite superiore ∞`;
      }
      if (!r.infinito) {
        if (r.quantitaA === null) return `Riga ${i + 1}: inserisci il limite superiore`;
        if (r.quantitaA <= r.quantitaDa) return `Riga ${i + 1}: il limite superiore deve essere > ${r.quantitaDa}`;
      }
      if (i > 0 && r.quantitaDa !== modalRighe[i - 1].quantitaA) {
        return `Riga ${i + 1}: i range devono essere continui`;
      }
    }
    return null;
  }

  // ── Salvataggio ───────────────────────────────────────────────────────────────

  async function salva() {
    const errore = valida();
    if (errore) { showToast(errore, 'error'); return; }
    if (!filterSocietaId || !modalFormatoId) return;

    setSaving(true);
    try {
      // @api POST /api/sfridi/salva { societaId, formatoArticoloId, righe } -> [{ id, quantitaDa, quantitaA, sfrido }]
      await apiFetch('/api/sfridi/salva', {
        method: 'POST',
        body: JSON.stringify({
          societaId: Number(filterSocietaId),
          formatoArticoloId: modalFormatoId,
          righe: modalRighe.map(({ quantitaDa, quantitaA, infinito, sfrido }) => ({
            quantitaDa,
            quantitaA: infinito ? null : quantitaA,
            sfrido,
          })),
        }),
      });
      showToast('Sfridi salvati', 'success');
      setShowModal(false);
      caricaLista();
    } catch {
      showToast('Errore nel salvataggio degli sfridi', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="sfridi-page">
        {/* Header */}
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Sfridi</h2>
            <p>Configurazione sfridi per formato articolo</p>
          </div>
          <button className="bop-btn bop-btn-primary" onClick={apriCrea}>
            <PlusIcon />
            Nuovi sfridi
          </button>
        </div>

        {/* Filtri (solo combo società se !isUnica) */}
        {!isUnica && (
          <div className="bop-card sfridi-filter-card">
            <div className="sfridi-filter-row">
              <select
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
                className="bop-input sfridi-select-societa"
              >
                <option value="">Tutte le società</option>
                {societaList.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabella principale */}
        <div className="bop-card">
          {!pronta ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Seleziona una società per continuare</p>
            </div>
          ) : loading ? (
            <div className="sfridi-loading">Caricamento...</div>
          ) : lista.length === 0 ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Nessun formato configurato</p>
            </div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Formato</th>
                    <th>Sfridi</th>
                    <th className="sfridi-th-actions">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((record) => (
                    <tr key={record.formatoArticoloId}>
                      <td className="sfridi-td-formato">{record.formatoNome}</td>
                      <td className="sfridi-td-riepilogo">
                        <span className="sfridi-riepilogo-text">
                          {stringaRiepilogo(record.righe)}
                        </span>
                      </td>
                      <td className="bop-text-right">
                        <div className="sfridi-actions">
                          {can('archivi.sfridi', 'edit') && (
                            <button
                              className="bop-btn-icon"
                              onClick={() => apriModifica(record)}
                              title="Modifica"
                            >
                              <EditIcon />
                            </button>
                          )}
                          {can('archivi.sfridi', 'delete') && (
                            <button
                              className="bop-btn-icon sfridi-btn-delete"
                              onClick={() => eliminaRecord(record)}
                              title="Elimina"
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modale */}
        {showModal && (
          <div className="bop-modal-overlay">
            <div className="bop-modal sfridi-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">
                  {modalMode === 'create' ? 'Nuovi sfridi' : `Modifica sfridi — ${modalFormatoNome}`}
                </h3>
                <button className="bop-btn-icon" onClick={chiudiModal}>
                  <XIcon />
                </button>
              </div>

              <div className="bop-modal__body">
                {/* Combo formato (solo in creazione) */}
                {modalMode === 'create' && (
                  <div className="bop-form-group" ref={formatoDropRef}>
                    <label className="bop-label">
                      Formato articolo <span className="bop-text-danger">*</span>
                    </label>
                    <div className="sfridi-autocomplete">
                      <input
                        className="bop-input"
                        placeholder="Digita per cercare..."
                        value={formatoQuery}
                        onChange={handleFormatoQueryChange}
                        onFocus={handleFormatoFocus}
                        autoComplete="off"
                        autoFocus
                      />
                      {formatoDropOpen && (
                        <div className="sfridi-autocomplete__dropdown">
                          {formatoResults.length === 0 ? (
                            <div className="sfridi-autocomplete__empty">Nessun formato disponibile</div>
                          ) : (
                            formatoResults.map(fmt => (
                              <div
                                key={fmt.id}
                                className={`sfridi-autocomplete__option${modalFormatoId === fmt.id ? ' sfridi-autocomplete__option--selected' : ''}`}
                                onMouseDown={() => selezionaFormato(fmt)}
                              >
                                {fmt.nome}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tabella range */}
                <div className="sfridi-range-section">
                  <table className="bop-table sfridi-range-table">
                    <thead>
                      <tr>
                        <th className="sfridi-range-th-da">DA (qtà)</th>
                        <th className="sfridi-range-th-a">A (qtà)</th>
                        <th className="sfridi-range-th-inf">∞</th>
                        <th className="sfridi-range-th-sfrido">Sfrido %</th>
                        <th className="sfridi-range-th-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalRighe.length === 0 ? (
                        <tr>
                          <td colSpan={5}>
                            <div className="bop-empty-state">Nessun range — clicca "+ Aggiungi range"</div>
                          </td>
                        </tr>
                      ) : (
                        modalRighe.map((riga, idx) => (
                          <tr key={riga._key}>
                            {/* DA — readonly */}
                            <td className="sfridi-range-td-da">
                              <input
                                className="bop-input sfridi-range-input-readonly"
                                type="number"
                                value={riga.quantitaDa}
                                readOnly
                                tabIndex={-1}
                              />
                            </td>

                            {/* A — input numerico, disabilitato se ∞ */}
                            <td className="sfridi-range-td-a">
                              <input
                                className="bop-input"
                                type="number"
                                min={riga.quantitaDa + 1}
                                value={riga.quantitaA ?? ''}
                                onChange={(e) => aggiornaQuantitaA(riga._key, e.target.value)}
                                disabled={riga.infinito}
                                placeholder={riga.infinito ? '∞' : ''}
                              />
                            </td>

                            {/* Checkbox ∞ */}
                            <td className="sfridi-range-td-inf">
                              <input
                                className="sfridi-range-checkbox"
                                type="checkbox"
                                checked={riga.infinito}
                                onChange={() => toggleInfinito(riga._key)}
                                title="Nessun limite superiore"
                              />
                            </td>

                            {/* Sfrido */}
                            <td className="sfridi-range-td-sfrido">
                              <input
                                className="bop-input"
                                type="number"
                                min={0}
                                max={100}
                                value={riga.sfrido}
                                onChange={(e) => aggiornaSfrido(riga._key, e.target.value)}
                              />
                            </td>

                            {/* Azioni */}
                            <td className="sfridi-range-td-actions">
                              <button
                                className="bop-btn-icon sfridi-btn-delete"
                                title="Elimina riga"
                                onClick={() => eliminaRiga(riga._key)}
                                disabled={modalRighe.length <= 1}
                                type="button"
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  <div className="sfridi-range-add">
                    <button
                      className="bop-btn bop-btn-secondary"
                      onClick={aggiungiRange}
                      type="button"
                    >
                      + Aggiungi range
                    </button>
                  </div>
                </div>
              </div>

              <div className="bop-modal__footer">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={chiudiModal}
                  disabled={saving}
                >
                  Annulla
                </button>
                {can('archivi.sfridi', 'edit') && (
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={salva}
                    disabled={saving}
                  >
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
